import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, Shield, Lock } from 'lucide-react'
import { authApi } from '@/lib/api/auth'
import { useAuthStore } from '@/store/authStore'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

export default function LoginPage() {
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<'sso' | 'totp'>('sso')
  const [pendingUserId, setPendingUserId] = useState('')
  const [error, setError] = useState('')
  const [otpValue, setOtpValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)

  useEffect(() => {
    const userId = searchParams.get('totp')
    if (userId) {
      setPendingUserId(userId)
      setStep('totp')
    }
  }, [searchParams])

  const handleMicrosoftLogin = async () => {
    setError('')
    setIsSubmitting(true)
    try {
      if (!localStorage.getItem('portal_device_id')) {
        localStorage.setItem('portal_device_id', crypto.randomUUID())
      }
      const res = await authApi.getMicrosoftUrl()
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to get login URL')
      sessionStorage.setItem('ms_oauth_state', res.data.state)
      window.location.href = res.data.url
    } catch (err: any) {
      setError(err?.error ?? err?.message ?? 'Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  const handleOtpComplete = async (code: string) => {
    if (isSubmitting) return
    setError('')
    setIsSubmitting(true)
    try {
      const response = await authApi.verify2FA(pendingUserId, code)
      if (!response.success || !response.data) {
        throw { error: response.error, code: 'UNKNOWN' }
      }
      const data = response.data
      setAuth(data.user!, data.accessToken!, data.refreshToken!, data.modules ?? [])
      navigate(data.user?.mustChangePassword ? '/change-password' : '/dashboard', { replace: true })
    } catch (err: any) {
      if (err?.code === 'INVALID_2FA') {
        setError('Invalid verification code. Please try again.')
      } else {
        setError(err?.error ?? 'Something went wrong. Please try again.')
      }
      setOtpValue('')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'linear-gradient(135deg, #071428 0%, #0B2545 55%, #0d1f3c 100%)' }}
    >
      {/* Left panel — branding (desktop only) */}
      <div className="hidden lg:flex lg:w-[44%] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 65%)' }}
          />
          <div
            className="absolute top-1/2 -right-20 w-80 h-80 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.07) 0%, transparent 65%)' }}
          />
          <div
            className="absolute -bottom-20 left-1/3 w-72 h-72 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(147,197,253,0.06) 0%, transparent 65%)' }}
          />
        </div>

        <div className="relative">
          <img src="/logo.png" alt="Monarch Innovation" className="h-9 w-auto" style={{ filter: 'brightness(0) invert(1) opacity(0.85)' }} />
        </div>

        <div className="relative space-y-8">
          <div>
            <h1 className="text-[38px] font-bold text-white leading-[1.15] tracking-tight">
              Corporate<br />Payment Portal
            </h1>
            <p className="mt-4 text-[15px] text-blue-200/60 leading-relaxed max-w-[280px]">
              Secure, compliant payments powered by ICICI Corporate Banking APIs
            </p>
          </div>

          <div className="space-y-3">
            {[
              { icon: Shield, label: 'Bank-grade RSA encryption on every transaction' },
              { icon: Lock, label: 'Multi-factor authentication & device trust' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.2)' }}
                >
                  <Icon className="h-3.5 w-3.5 text-blue-400" />
                </div>
                <span className="text-[13px] text-blue-100/55 leading-snug">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-[11px] text-blue-200/25">
          © {new Date().getFullYear()} Monarch Innovation. All rights reserved.
        </p>
      </div>

      {/* Divider */}
      <div
        className="hidden lg:block w-px self-stretch my-10"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.06) 20%, rgba(255,255,255,0.06) 80%, transparent)' }}
      />

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-16">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8 flex flex-col items-center gap-2">
          <img src="/icon.png" alt="Monarch Innovation" className="h-10 w-10 object-contain" />
          <p className="text-[13px] text-blue-200/40">Corporate Payment Portal</p>
        </div>

        <div className="w-full max-w-[390px]">
          <div
            className="rounded-2xl p-8"
            style={{
              background: 'rgba(255,255,255,0.975)',
              boxShadow: '0 32px 64px -16px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)',
            }}
          >
            {step === 'sso' ? (
              <>
                <div className="mb-7">
                  <h2 className="text-[21px] font-semibold text-gray-900 tracking-tight leading-snug">
                    Welcome back
                  </h2>
                  <p className="text-[13.5px] text-gray-500 mt-1.5">
                    Sign in with your company Microsoft account
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive" className="mb-5">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-3 px-4 py-[11px] rounded-xl border text-[14px] font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    borderColor: '#d1d5db',
                    background: isSubmitting ? '#f9fafb' : 'white',
                    color: '#374151',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
                  }}
                  onMouseEnter={e => { if (!isSubmitting) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb' }}
                  onMouseLeave={e => { if (!isSubmitting) (e.currentTarget as HTMLButtonElement).style.background = 'white' }}
                >
                  {isSubmitting ? (
                    <Loader2 className="h-[18px] w-[18px] animate-spin text-gray-400 flex-shrink-0" />
                  ) : (
                    <MicrosoftIcon className="h-[18px] w-[18px] flex-shrink-0" />
                  )}
                  <span>{isSubmitting ? 'Redirecting to Microsoft…' : 'Continue with Microsoft'}</span>
                </button>

                <div className="mt-6 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-4 text-[11.5px] text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <Lock className="h-3 w-3" />
                      <span>AES-256 Encrypted</span>
                    </div>
                    <div className="w-px h-3 bg-gray-200" />
                    <div className="flex items-center gap-1.5">
                      <Shield className="h-3 w-3" />
                      <span>ICICI Corporate API</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-7">
                  <div className="w-10 h-10 rounded-xl mb-5 overflow-hidden">
                    <img src="/icon.png" alt="Monarch" className="w-full h-full object-contain" />
                  </div>
                  <h2 className="text-[21px] font-semibold text-gray-900 tracking-tight leading-snug">
                    Two-factor authentication
                  </h2>
                  <p className="text-[13.5px] text-gray-500 mt-1.5">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive" className="mb-5">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpValue}
                    onChange={(value) => {
                      setOtpValue(value)
                      if (value.length === 6) handleOtpComplete(value)
                    }}
                    disabled={isSubmitting}
                    autoFocus
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {isSubmitting && (
                  <div className="flex items-center justify-center gap-2 mt-5 text-[13px] text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Verifying…
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => { setStep('sso'); setError(''); setOtpValue('') }}
                  className="mt-6 w-full text-center text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ← Back to sign in
                </button>
              </>
            )}
          </div>

          <p className="text-center text-[11px] text-blue-200/20 mt-5">
            Access restricted to authorised personnel only
          </p>
        </div>
      </div>
    </div>
  )
}
